import { useInfiniteQuery, useQuery } from 'react-query';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { representativesDao } from 'shared/dao/representativesDao';
import {
  IAgenciesPaginationQuery,
  IAgenciesPaginationRequestPayload,
  IRepresentativesProfilePayload,
} from 'shared/interfaces/IRepresentatives';
import { profileService } from './profileService';
import {
  IRepresentativesSingleProfilePayload,
  ISingleRepresentativeAttachedMediaPayload,
  ISingleRepresentativeAttachedMediaPayloadResponse,
  ISingleRepresentativeHiddenAMediaPayload,
  ISingleRepresentativeHiddenMediaPayloadResponse,
} from 'shared/interfaces/IRepresentativesEditProfile';

const {
  getMultipleRepresentativesProfile: getMultipleRepresentativesProfileDao,
  getAgenciesWithPagination: getAgenciesWithPaginationDao,
  getSingleRepresentativeProfile: getSingleRepresentativeProfileDao,
  getSingleRepresentativeAttachedMediaGQL: getSingleRepresentativeAttachedMediaGQLDao,
  getSingleRepresentativeHiddenMediaGQL: getSingleRepresentativeHiddendMediaGQLDao,
} = representativesDao();
const { createProfile, updateProfile, archiveProfile } = profileService();

export const representativeService = () => {
  const getRepresentativesProfile = () => {
    return useQuery<IRepresentativesProfilePayload>(
      [REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile],
      () => getMultipleRepresentativesProfileDao(),
    );
  };

  const updateRepresentativeProfile = updateProfile;

  const getAgenciesWithPagination = (agencyQuery: IAgenciesPaginationQuery) => {
    return useInfiniteQuery<IAgenciesPaginationRequestPayload>({
      /* 
        Only re-renders if the country is changed and the search;
        country: it needs to re-render because every country has different agencies
        search: needs to fetch base on the client inputs, need to use debounce for optimization
      */
      queryKey: [
        REPRESENTATIVES_QUERY_STRING.representatives_agencies_pagination,
        agencyQuery?.country,
        agencyQuery?.search,
      ],
      queryFn: ({ pageParam = '' }) => getAgenciesWithPaginationDao({ ...agencyQuery, after: pageParam }),
      getNextPageParam: (lastPage) => {
        if (!lastPage.data) return undefined;
        return lastPage.data.agencies_connection.pageInfo.hasNextPage
          ? lastPage.data.agencies_connection.pageInfo.endCursor
          : undefined;
      },
      enabled: Boolean(agencyQuery?.search),
    });
  };

  const getSingleRepresentativeProfile = (id: string) => {
    return useQuery<IRepresentativesSingleProfilePayload>({
      queryKey: [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, id],
      queryFn: () => getSingleRepresentativeProfileDao(id),
      enabled: Boolean(id),
    });
  };

  const createRepresentatives = createProfile;

  const getSingleRepresentativeAttachedMediaGQL = (payload: ISingleRepresentativeAttachedMediaPayload) => {
    return useQuery<ISingleRepresentativeAttachedMediaPayloadResponse>({
      queryKey: [
        REPRESENTATIVES_QUERY_STRING.representatives_single_profile_attached_media,
        payload.mediumable_id,
        payload.file_type,
      ],
      queryFn: () => getSingleRepresentativeAttachedMediaGQLDao(payload),
      enabled: Boolean(payload.mediumable_id),
    });
  };

  const deleteRepresentative = archiveProfile;

  const getSingleRepresentativeHiddendMediaGQL = (payload: ISingleRepresentativeHiddenAMediaPayload) => {
    return useQuery<ISingleRepresentativeHiddenMediaPayloadResponse>({
      queryKey: [REPRESENTATIVES_QUERY_STRING.representatives_single_profile_hidden_media, payload.profile_id],
      queryFn: () => getSingleRepresentativeHiddendMediaGQLDao(payload),
      enabled: Boolean(payload.profile_id),
    });
  };

  return {
    getRepresentativesProfile,
    updateRepresentativeProfile,
    getAgenciesWithPagination,
    getSingleRepresentativeProfile,
    createRepresentatives,
    getSingleRepresentativeAttachedMediaGQL,
    deleteRepresentative,
    getSingleRepresentativeHiddendMediaGQL,
  };
};
